/** @typedef {import("../../api/types").Notification} Notification */
import React, {useCallback, useEffect, useState} from "react";
import axios from "axios";
import {Link} from "react-router-dom";
import {Alert, Button, Container, ListGroup, ListGroupItem, Spinner} from "reactstrap";
import {useDebouncedSearch} from "../../hooks/useDebouncedSearch";
import {useListParams} from "../../hooks/useListParams";
import {useScrollRestore} from "../../hooks/useScrollRestore";
import SearchBox from "../../shared/components/SearchBox";
import StatusFilter from "../../shared/components/StatusFilter";
import OrderStatusBadge from "../orders/components/OrderStatusBadge";
import IconLabel from "../../shared/components/IconLabel";
import {BellIcon, CheckIcon, ChevronLeftIcon, ChevronRightIcon} from "../../shared/components/icons";
import "./Notifications.css";
import {formatAgo, formatDateTime} from "../../shared/utils/format";
import {notificationText, notificationsChanged, onNotificationsChanged} from "../../shared/utils/notifications";

const PAGE_SIZE = 25;
const URL = "/os/api/notifications/";
// What it shows to begin with: what has not been read yet.
const DEFAULT_FILTER = ["unread"];
// The list's "status" slot holds what to show: the kinds of news ticked (none
// ticked: all) and, as one more option, only what is unread.
const UNREAD = "unread";
const OPTIONS = [
    {code: UNREAD, node: <span>Não lidos</span>},
    {code: "confirmed", node: <OrderStatusBadge status="Confirmed" />},
    {code: "canceled", node: <OrderStatusBadge status="Canceled" />},
    {code: "expired", node: <OrderStatusBadge status="Expired" />},
];
const FILTER_CODES = OPTIONS.map((o) => o.code);

/**
 * Everything the customers and the system did to the provider's orders (the
 * bell shows only the latest few): newest first, unread in bold, filterable to
 * the unread. Opening an order marks its news as read (see OrderDetail).
 */
const Notifications = () => {
    const {page, search, statuses, setPage, setSearch, setStatuses} = useListParams({
        basePath: "/notifications",
        defaultOrdering: "-created_at",
        statusCodes: FILTER_CODES,
        defaultStatuses: DEFAULT_FILTER,
    });
    // The request waits for a pause in typing; a new search starts from page 1.
    const {text: searchText, setText: setSearchText} = useDebouncedSearch(search, setSearch);
    const onlyUnread = statuses.includes(UNREAD);
    const kindKey = statuses.filter((code) => code !== UNREAD).join(",");
    const [data, setData] = useState(/** @type {{results: Notification[], pagination: any}|null} */ (null));
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(/** @type {string|null} */ (null));
    const [busy, setBusy] = useState(false);
    const [reload, setReload] = useState(0);

    // A mark-as-read elsewhere (the bell, an order) makes this list stale too.
    useEffect(() => onNotificationsChanged(() => setReload((n) => n + 1)), []);

    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);
        setError(null);
        axios
            .get(URL, {
                params: {
                    page,
                    page_size: PAGE_SIZE,
                    search: search || undefined,
                    unread: onlyUnread || undefined,
                    kind: kindKey ? kindKey.split(",") : undefined,
                },
                paramsSerializer: {indexes: null},
                signal: controller.signal,
            })
            .then(({data}) => setData(data))
            .catch((err) => {
                if (axios.isCancel(err)) return;
                setError("Não foi possível carregar os avisos.");
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });
        return () => controller.abort();
    }, [page, search, onlyUnread, kindKey, reload]);

    useScrollRestore(!loading);

    const markAllRead = useCallback(async () => {
        setBusy(true);
        try {
            await axios.post(`${URL}read_all/`);
            notificationsChanged();
        } catch {
            setError("Não foi possível marcar os avisos como lidos.");
        } finally {
            setBusy(false);
        }
    }, []);

    const items = data?.results ?? [];
    const pagination = data?.pagination;
    const hasUnread = items.some((item) => !item.read);

    return (
        <Container className="mt-4 mb-5">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <h1 className="h4 m-0">
                    <IconLabel icon={<BellIcon size={20} />}>Avisos</IconLabel>
                </h1>
                <div className="d-flex flex-wrap align-items-center gap-2">
                    <StatusFilter
                        options={OPTIONS}
                        selected={statuses}
                        onChange={setStatuses}
                        label="Mostrar"
                        allLabel="Todos os avisos"
                    />
                    <SearchBox
                        value={searchText}
                        onChange={setSearchText}
                        placeholder="Buscar por cliente ou nº da ordem"
                        ariaLabel="Buscar avisos"
                    />
                    <Button color="primary" outline disabled={busy || !hasUnread} onClick={markAllRead}>
                        <IconLabel icon={<CheckIcon />}>Marcar todos como lidos</IconLabel>
                    </Button>
                </div>
            </div>
            {error && <Alert color="danger">{error}</Alert>}
            {loading && !data && (
                <div className="text-center py-4">
                    <Spinner />
                </div>
            )}
            {!error && !loading && items.length === 0 && (
                <p className="text-muted">
                    {statuses.length
                        ? onlyUnread && statuses.length === 1
                            ? "Nenhum aviso não lido."
                            : "Nenhum aviso com esse filtro."
                        : "Nenhum aviso."}
                </p>
            )}
            {items.length > 0 && (
                <ListGroup className="mb-3">
                    {items.map((item) => (
                        <ListGroupItem
                            key={item.id}
                            tag={Link}
                            action
                            to={`/orders/${item.order_id}`}
                            state={{backTo: "/notifications"}}
                            className="d-flex justify-content-between align-items-center gap-3"
                        >
                            <span className={item.read ? "" : "fw-semibold"}>
                                {!item.read && (
                                    <span className="notification-dot me-2" role="img" aria-label="Não lido" />
                                )}
                                {notificationText(item)}
                            </span>
                            <span className="small text-muted text-nowrap" title={formatDateTime(item.created_at)}>
                                {formatAgo(item.created_at)}
                            </span>
                        </ListGroupItem>
                    ))}
                </ListGroup>
            )}
            {pagination && pagination.total_pages > 1 && (
                <div className="d-flex justify-content-between align-items-center">
                    <Button
                        color="primary"
                        outline
                        disabled={loading || !pagination.prev_page}
                        onClick={() => setPage(pagination.prev_page)}
                    >
                        <IconLabel icon={<ChevronLeftIcon />}>Anterior</IconLabel>
                    </Button>
                    <span>
                        Página {pagination.curr_page} de {pagination.total_pages}
                    </span>
                    <Button
                        color="primary"
                        outline
                        disabled={loading || !pagination.next_page}
                        onClick={() => setPage(pagination.next_page)}
                    >
                        <IconLabel icon={<ChevronRightIcon />}>Próxima</IconLabel>
                    </Button>
                </div>
            )}
        </Container>
    );
};

export default Notifications;
